import React, { useState, useEffect } from 'react';
import axiosInstance, { BACKEND_ROOT_URL } from '../../utils/axiosInstance';
import Alert from '../components/Alert';
import { EditIcon } from 'lucide-react';

const CategoryManager = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- Create State ---
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategorySequence, setNewCategorySequence] = useState(0);
  const [newCategoryImage, setNewCategoryImage] = useState(null); // State for new image

  // --- Edit State ---
  const [editingCategory, setEditingCategory] = useState({ id: null, name: '', sequence: 0 });
  const [editingCategoryImage, setEditingCategoryImage] = useState(null); // State for editing image

  const [alert, setAlert] = useState({ message: null, type: 'success' });

  // Base URL for images (Adjust based on where your PHP serves files)
  // Assuming your API is at http://localhost/api/, images might be at http://localhost/api/uploads/...
  // Or simply empty string if path stored in DB is relative and root is correct.
  

  const showSuccessAlert = (message) => setAlert({ message, type: 'success' });
  const showErrorAlert = (message) => setAlert({ message, type: 'error' });
  const handleCloseAlert = () => setAlert({ message: null });

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/categories');
      setCategories(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch categories.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [alert]);


  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!newCategoryName.trim()) return;

    try {
      // --- Use FormData for File Upload ---
      const formData = new FormData();
      formData.append('name', newCategoryName);
      formData.append('sequence', newCategorySequence);
      if (newCategoryImage) {
        formData.append('image', newCategoryImage);
      }

      await axiosInstance.post('/categories', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Reset Form
      setNewCategoryName('');
      setNewCategorySequence(0);
      setNewCategoryImage(null);
      // Reset file input visually
      document.getElementById('create-cat-file').value = ""; 
      
      fetchCategories();
      showSuccessAlert("Category created successfully");
    } catch (err) {
      console.error(err);
      showErrorAlert('Failed to create category.');
    }
  };

  const handleUpdateCategory = async (id) => {
    if (!editingCategory.name.trim()) return;

    try {
      // --- Use FormData for Update ---
      const formData = new FormData();
      formData.append('name', editingCategory.name);
      formData.append('sequence', editingCategory.sequence);
      
      // Only append image if a new one is selected
      if (editingCategoryImage) {
        formData.append('image', editingCategoryImage);
      }

      // Important: Using POST for update to handle $_FILES in PHP easily
      // You might need to adjust your router to accept POST on the update route
      // or send formData.append('_method', 'PUT') if your backend supports that.
     const response =  await axiosInstance.put(`/categories/${id}`, formData,{
        headers: { 'Content-Type': 'multipart/form-data' }
      });

     console.log("response", response)
      setEditingCategory({ id: null, name: '', sequence: 0 });
      setEditingCategoryImage(null);
      fetchCategories();
      showSuccessAlert("Category updated successfully");
    } catch (err) {
      console.error(err);
      showErrorAlert('Failed to update category.');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm("Are you sure you want to delete this category?")) return;

    try {
      await axiosInstance.delete(`/categories/${id}`);
      showSuccessAlert("Category deleted successfully!");
      fetchCategories(); // Refresh list to remove deleted item
    } catch (error) {
      console.error('Delete error:', error);
      showErrorAlert(error.response?.data?.message || "Error in delete! Try Later.");
    }
  };

  useEffect(()=>{
    console.log("Editing category", editingCategory)
  },[editingCategory])
  if (loading) return <p>Loading categories...</p>;

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-4">Manage Categories</h2>

      <Alert message={alert.message} type={alert.type} onClose={handleCloseAlert} />

      {/* --- CREATE FORM --- */}
      <form onSubmit={handleCreateCategory} className="flex flex-col md:flex-row items-end gap-2 mb-6 p-4 bg-gray-50 border rounded-lg">
        <div className="flex-grow w-full">
          <label className="block text-sm font-medium text-gray-700">Category Name</label>
          <input
            type="text"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            className="mt-1 w-full p-2 border border-gray-300 rounded"
          />
        </div>
        <div className="w-full md:w-24">
          <label className="block text-sm font-medium text-gray-700">Sequence</label>
          <input
            type="number"
            value={newCategorySequence}
            onChange={(e) => setNewCategorySequence(Number(e.target.value))}
            className="mt-1 w-full p-2 border border-gray-300 rounded"
          />
        </div>
        <div className="w-full md:w-auto">
           <label className="block text-sm font-medium text-gray-700">Image</label>
           <input 
             id="create-cat-file"
             type="file" 
             onChange={(e) => setNewCategoryImage(e.target.files[0])}
             className="mt-1 w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
           />
        </div>
        <button type="submit" className="bg-blue-500 text-white py-2 px-6 rounded hover:bg-blue-600 h-10 mt-4 md:mt-0">
          Add
        </button>
      </form>

      {/* --- LIST --- */}
      <ul className="space-y-3">
        {categories.map((category) => (
          <li key={category.id} className="flex flex-col sm:flex-row items-center justify-between p-3 bg-gray-50 rounded border">
            
            {editingCategory.id === category.id ? (
              // --- EDIT VIEW ---
              <div className="flex-grow flex flex-col sm:flex-row items-center gap-2 w-full">
                 {/* Existing Image Preview in Edit Mode */}
                 {category.image && !editingCategoryImage && (
                    <img 
                        src={`${BACKEND_ROOT_URL}/${category.image}`} 
                        alt="current" 
                        className="w-10 h-10 object-cover rounded" 
                    />
                )}
                
                <input
                  type="text"
                  value={editingCategory.name}
                  onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                  className="flex-grow p-2 border border-blue-500 rounded w-full sm:w-auto"
                  placeholder="Name"
                />
                <input
                  type="number"
                  value={editingCategory.sequence}
                  onChange={(e) => setEditingCategory({ ...editingCategory, sequence: Number(e.target.value) })}
                  className="w-full sm:w-20 p-2 border border-blue-500 rounded"
                  placeholder="Seq"
                />
                {/* File Upload for Edit */}
                <input 
                    type="file"
                    onChange={(e) => setEditingCategoryImage(e.target.files[0])}
                    className="text-xs w-full sm:w-auto"
                />

                <div className="flex gap-2 mt-2 sm:mt-0">
                    <button onClick={() => handleUpdateCategory(category.id)} className="bg-green-500 text-white py-1 px-3 rounded text-sm">Save</button>
                    <button onClick={() => { 
                        setEditingCategory({ id: null, name: '', sequence: 0 }); 
                        setEditingCategoryImage(null); 
                    }} className="bg-gray-400 text-white py-1 px-3 rounded text-sm">Cancel</button>
                </div>
              </div>
            ) : (
              // --- DEFAULT VIEW ---
              <>
                <div className="flex items-center gap-4 w-full">
                  <span className="text-gray-500 text-sm font-mono w-6 text-center">{category.sequence}</span>
                  
                  {/* Display Image */}
                  {category.image ? (
                     <img 
                        src={`${BACKEND_ROOT_URL}/${category.image}`} 
                        alt={category.name} 
                        className="w-10 h-10 object-cover rounded border border-gray-200" 
                     />
                  ) : (
                      <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center text-xs text-gray-500">No Img</div>
                  )}

                  <span className="text-gray-800 font-medium">{category.name}</span>
                </div>
                
                <div className="flex gap-2 mt-2 sm:mt-0 min-w-fit">
                  <button onClick={() => setEditingCategory({ id: category.id, name: category.name, sequence: category.sequence })} className="text-blue-500 hover:underline text-sm px-2">
                    Edit
                  </button>
                  <button onClick={() => handleDeleteCategory(category.id)} className="text-red-500 hover:underline text-sm px-2">
                    Delete
                  </button>
                </div>
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default CategoryManager;